import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { XCircle } from "lucide-react";
import Lantern from "../components/Lantern";
import Purse from "../components/Purse";

const TransitAnimation = () => (
  <div className="relative h-24 my-2" data-testid="transit-anim">
    {/* Purse on left */}
    <div className="absolute left-[10%] top-1/2 -translate-y-1/2">
      <Purse size={56} />
    </div>
    {/* Dotted path */}
    <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
      <line x1="25" y1="50" x2="75" y2="50" stroke="#FBBF24" strokeWidth="1" className="transit-path" />
    </svg>
    {/* Gold coins gliding */}
    <span className="transit-coin" />
    <span className="transit-coin delay-1" />
    <span className="transit-coin delay-2" />
    {/* Lantern on right */}
    <div className="absolute right-[10%] top-1/2 -translate-y-1/2 w-14 h-14 rounded-xl bg-[var(--ra-primary)] flex items-center justify-center">
      <Lantern size={32} className="text-white" glow />
    </div>
  </div>
);

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const session_id = params.get("session_id");
  const transfer_id = params.get("transfer_id");
  const [state, setState] = useState({ kind: "polling", attempt: 0 });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!session_id) { setState({ kind: "failed" }); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/status/${session_id}`);
        if (r.data.status === "paid") {
          setState({ kind: "paid", transfer_id: r.data.transfer_id || transfer_id });
          return;
        }
        if (r.data.status === "expired" || r.data.status === "failed") {
          setState({ kind: "failed" });
          return;
        }
        attemptsRef.current += 1;
        setState({ kind: "polling", attempt: attemptsRef.current });
        if (attemptsRef.current < 8) setTimeout(poll, 2000);
        else setState({ kind: "timeout" });
      } catch {
        setState({ kind: "failed" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id, transfer_id]);

  return (
    <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="payment-result">
      <div className="ra-card p-10">
        {state.kind === "polling" && (
          <>
            <TransitAnimation />
            <h1 className="font-heading text-2xl font-bold mt-4">Your money is on its way…</h1>
            <p className="text-sm text-[var(--ra-muted)] mt-2">We&apos;re confirming with the network ({state.attempt + 1}/8)</p>
          </>
        )}
        {state.kind === "paid" && (
          <div data-testid="payment-paid">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-[var(--ra-primary)] flex items-center justify-center lantern-ping" data-testid="success-lantern">
              <Lantern size={42} className="text-white" glow />
            </div>
            <h1 className="font-heading text-2xl font-bold mt-5">Transfer sent — you lit up their day!</h1>
            <p className="text-[var(--ra-muted)] mt-2">Your loved one will receive the funds shortly. We&apos;ve sent a receipt to your account.</p>
            <div className="mt-6 flex flex-col gap-2">
              <Button className="btn-primary rounded-md" onClick={() => nav("/history")} data-testid="paid-view-history">View receipt</Button>
              <Link to="/send"><Button variant="outline" className="rounded-md w-full">Send another</Button></Link>
            </div>
          </div>
        )}
        {(state.kind === "failed" || state.kind === "timeout") && (
          <div data-testid="payment-failed">
            <XCircle className="mx-auto text-[var(--ra-error)]" size={48} />
            <h1 className="font-heading text-2xl font-bold mt-4">{state.kind === "timeout" ? "Still processing…" : "Payment failed"}</h1>
            <p className="text-[var(--ra-muted)] mt-2">{state.kind === "timeout" ? "We're still waiting on confirmation. Check History in a moment." : "Something went wrong. Please try again."}</p>
            <Button className="btn-primary rounded-md mt-6" onClick={() => nav("/send")}>Back to Send Money</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
